import React from 'react';
import { useTranslation } from 'localization';
import { ChannelItem, ChannelItemType } from 'types/channel';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';

export interface ChannelItemCardMoleculeProps {
  item: ChannelItem;
  /** Name of the source channel when `item.is_syndicated` is true. Required to render the badge. */
  sourceChannelName?: string;
  /** When provided, the card becomes interactive (click + Enter/Space keyboard activation). */
  onClick?: (item: ChannelItem) => void;
  className?: string;
}

const ITEM_TYPE_ICONS: Record<ChannelItemType, string> = {
  approach: '📋',
  surface_template: '🖼️',
  assistant: '🤖',
  video: '🎬',
  form: '📝',
  metaspace: '🗂️',
  schema: '📐',
  context: '🔍',
  arc_template: '🌀',
  orbit_template: '🪐',
  aspect: '🏷️',
  // EAINT-864 Phase 1 registration; Phase 3 gives aspect_group its own dedicated
  // icon/glyph (design-system icon map work) — placeholder mirrors 'aspect' meanwhile.
  aspect_group: '🏷️',
  composition: '🧩',
  archetype: '🏷️',
};

const ITEM_TYPE_KEYS: Record<ChannelItemType, string> = {
  approach: 'marketplace.common.itemType.approach',
  surface_template: 'marketplace.common.itemType.surfaceTemplate',
  assistant: 'marketplace.common.itemType.assistant',
  video: 'marketplace.common.itemType.video',
  form: 'marketplace.common.itemType.form',
  metaspace: 'marketplace.common.itemType.metaspace',
  schema: 'marketplace.common.itemType.schema',
  context: 'marketplace.common.itemType.context',
  arc_template: 'marketplace.common.itemType.arcTemplate',
  orbit_template: 'marketplace.common.itemType.orbitTemplate',
  aspect: 'marketplace.common.itemType.aspect',
  aspect_group: 'marketplace.common.itemType.aspectGroup',
  composition: 'marketplace.common.itemType.composition',
  archetype: 'marketplace.common.itemType.archetype',
};

export const ChannelItemCardMolecule: React.FC<ChannelItemCardMoleculeProps> = ({
  item,
  sourceChannelName,
  onClick,
  className = '',
}) => {
  const { t } = useTranslation();
  const isSyndicated = Boolean(item.is_syndicated);
  const showBadge = isSyndicated && Boolean(sourceChannelName);
  const isClickable = Boolean(onClick);

  const classes = [
    'marketplace-item-card',
    isSyndicated ? 'marketplace-item-card--syndicated' : '',
    isClickable ? 'marketplace-item-card--clickable' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const displayName = getLocalizedText(item.item_metadata?.name) || item.item_type;
  const displayDescription = getLocalizedText(item.item_metadata?.description);
  const tags = item.item_metadata?.tags || [];

  const handleClick = () => {
    if (onClick) onClick(item);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick(item);
    }
  };

  return (
    <div
      className={classes}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? handleClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
    >
      {showBadge && (
        <div className="marketplace-item-card__badge-slot">
          <StatusBadgeAtom
            tone="info"
            icon="pi pi-link"
            label={t('marketplace.content.syndicatedBadge')}
            tooltip={t('marketplace.content.syndicatedFrom', {
              sourceChannelName: sourceChannelName as string,
            })}
            size="small"
          />
        </div>
      )}
      <div className="marketplace-item-card__header">
        <span className="marketplace-item-card__icon" title={t(ITEM_TYPE_KEYS[item.item_type])}>
          {ITEM_TYPE_ICONS[item.item_type]}
        </span>
        <div className="marketplace-item-card__title-area">
          <h4 className="marketplace-item-card__name">{displayName}</h4>
          <div className="marketplace-item-card__meta">
            <span className="marketplace-item-card__type">
              {t(ITEM_TYPE_KEYS[item.item_type])}
            </span>
            {item.item_version != null && (
              <>
                <span className="marketplace-item-card__separator">·</span>
                <span className="marketplace-item-card__version">{t('marketplace.common.version', { version: item.item_version })}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {displayDescription && (
        <p className="marketplace-item-card__description">{displayDescription}</p>
      )}

      {tags.length > 0 && (
        <div className="marketplace-item-card__tags">
          {tags.map((tag) => (
            <span key={tag} className="marketplace-item-card__tag">
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
